import { SoundFile } from '@euler/assets';
import { VoiceRecordingOverlay } from '@euler/components/input/VoiceRecordingOverlay';
import { usePlaySoundEffect } from '@euler/functions/usePlaySoundEffect';
import {
  useVoiceRecording,
  VoiceRecordingResult,
} from '@euler/functions/useVoiceRecording';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

/** Default cap on a single recording session, in seconds. */
export const kDefaultMaxRecordingSeconds = 30;
/** Hard upper bound. Caller-supplied values are clamped to this. */
export const kMaxRecordingSecondsCeiling = 60;

export type VoiceRecordingCallbacks = {
  /** Called with the captured audio after the user taps "finish". */
  onComplete: (recording: VoiceRecordingResult) => void;
  /** Called when the user taps "cancel". Optional. */
  onCancel?: () => void;
  /**
   * Called when recording fails to start (permission denied, mic unavailable,
   * already-recording, etc.) or when stopAndUnload throws on completion.
   */
  onError?: (error: unknown) => void;
  /**
   * Maximum recording duration in seconds. The cap is mandatory: values
   * `<= 0` are treated as the {@link kDefaultMaxRecordingSeconds} default,
   * and any value above {@link kMaxRecordingSecondsCeiling} is clamped to
   * the ceiling. When reached, the recording is automatically stopped and
   * `onComplete` fires with the captured audio.
   */
  maxDurationSeconds?: number;
};

type VoiceRecorder = {
  /** Whether a recording session is currently active. */
  isRecording: boolean;
  /**
   * Begin a recording session. The callbacks fire when the session ends —
   * exactly one of `onComplete` / `onCancel` / `onError` is invoked.
   *
   * Only one session can be active at a time. Calling start while a session
   * is in flight invokes `onError` immediately with an "already recording"
   * error and does not interrupt the existing session.
   */
  start: (callbacks: VoiceRecordingCallbacks) => void;
};

const VoiceRecordingContext = createContext<VoiceRecorder | null>(null);

export const useVoiceRecorder = (): VoiceRecorder => {
  const ctx = useContext(VoiceRecordingContext);
  if (!ctx) {
    throw new Error(
      'useVoiceRecorder must be used inside <VoiceRecordingProvider />',
    );
  }
  return ctx;
};

export const VoiceRecordingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const recorder = useVoiceRecording();
  const [isRecording, setIsRecording] = useState(false);
  const isRecordingRef = useRef(false);
  isRecordingRef.current = isRecording;
  const callbacksRef = useRef<VoiceRecordingCallbacks | null>(null);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const playStartSound = usePlaySoundEffect(SoundFile.dingMetallic);

  const clearMaxDurationTimer = useCallback(() => {
    if (maxDurationTimerRef.current != null) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
  }, []);

  const onComplete = useCallback(async () => {
    clearMaxDurationTimer();
    const callbacks = callbacksRef.current;
    callbacksRef.current = null;
    setIsRecording(false);
    try {
      const result = await recorder.stop();
      callbacks?.onComplete(result);
    } catch (e) {
      callbacks?.onError?.(e);
    }
  }, [recorder, clearMaxDurationTimer]);

  const start = useCallback(
    (callbacks: VoiceRecordingCallbacks) => {
      if (isRecordingRef.current) {
        callbacks.onError?.(new Error('voice recording already in progress'));
        return;
      }
      callbacksRef.current = callbacks;
      // Mark recording active synchronously so the UI overlay shows
      // immediately. If the recorder fails to start, surface via onError.
      setIsRecording(true);
      void playStartSound();
      void (async () => {
        try {
          await recorder.start();
          // Arm the max-duration cap only after the recorder is actually
          // running so the wall-clock timer reflects audio time. The cap
          // is mandatory: clamp into [1, ceiling] and fall back to the
          // default for non-positive caller values.
          const requested = callbacks.maxDurationSeconds;
          const seconds = Math.min(
            kMaxRecordingSecondsCeiling,
            requested != null && requested > 0
              ? requested
              : kDefaultMaxRecordingSeconds,
          );
          if (callbacksRef.current === callbacks) {
            maxDurationTimerRef.current = setTimeout(() => {
              maxDurationTimerRef.current = null;
              if (callbacksRef.current === callbacks) {
                void onComplete();
              }
            }, seconds * 1000);
          }
        } catch (e) {
          callbacksRef.current = null;
          setIsRecording(false);
          callbacks.onError?.(e);
        }
      })();
    },
    [recorder, playStartSound, onComplete],
  );

  const onCancel = useCallback(async () => {
    clearMaxDurationTimer();
    const callbacks = callbacksRef.current;
    callbacksRef.current = null;
    setIsRecording(false);
    await recorder.cancel();
    callbacks?.onCancel?.();
  }, [recorder, clearMaxDurationTimer]);

  const value = useMemo<VoiceRecorder>(
    () => ({ isRecording, start }),
    [isRecording, start],
  );

  return (
    <VoiceRecordingContext.Provider value={value}>
      {children}
      <VoiceRecordingOverlay
        visible={isRecording}
        onCancel={onCancel}
        onComplete={onComplete}
      />
    </VoiceRecordingContext.Provider>
  );
};
